import {
  Component,
  computed,
  HostListener,
  inject,
  input,
  output,
} from '@angular/core';
import { PortCallModel } from '../../../../shared/models/port-call.model';
import { TranslateModule } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { VoyagePortCallStates } from '../../../../shared/enums/voyage-portcall-states.enum';
import { PortActivityTypes } from '../../../../shared/enums/port-activity-types.enum';
import { PortOvertimeLevel } from '../../../../shared/enums/port-overtime-level.enum';
import {
  DropdownComponent,
  DropdownService,
  TableAction,
  ConfirmationDialogService,
} from '@solcre-org/core-ui';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-voyage-routes-portcall',
  imports: [TranslateModule, DatePipe, DropdownComponent],
  templateUrl: './voyage-routes-portcall.html',
})
export class VoyageRoutesPortcall {
  //Inputs and outputs
  current = input<boolean>(false);
  next = input<boolean>(false);
  showing = input<boolean>(false);
  portCall = input<PortCallModel | null>(null);
  changeAsActive = output<void>();
  editPortCallAction = output<void>();
  editNauticalMileDistanceAction = output<PortCallModel>();
  deletePortCallAction = output<void>();
  changeStatus = output<PortCallModel>();
  addExpensesAction = output<PortCallModel>();
  expensesUpdated = output<PortCallModel>();
  addSpeedSelectionAction = output<PortCallModel>();
  speedSelectionUpdated = output<PortCallModel>();

  //Models
  voyagePortCallStates = VoyagePortCallStates;
  portActivityTypes = PortActivityTypes;
  portOvertimeLevel = PortOvertimeLevel;

  //Services
  dropdownService = inject(DropdownService);
  confirmService = inject(ConfirmationDialogService);
  translateService = inject(TranslateService);

  //Full "PORT OF X - TERMINAL, COUNTRY" label — shared by the card and compact
  //(xs) views, and reused as the native title attribute so the full name is
  //still reachable via tooltip once the visible text is truncated by CSS.
  portTitle = computed(() => {
    const details = this.portCall()?.portDetails;
    if (!details) return '';
    const name = details.terminal ? `${details.fullName} - ${details.terminal}` : details.fullName;
    return details.country ? `${name}, ${details.country}` : (name ?? '');
  });

  //Overtime badge: only OT1/OT2 are shown, OT2 prevails over OT1, and
  //regular/null/uncomputable snapshots show no badge.
  overtimeLevel = computed(() => {
    const highestLevel = this.portCall()?.overtimeSnapshot?.highestLevel;
    return highestLevel === PortOvertimeLevel.OT1 || highestLevel === PortOvertimeLevel.OT2
      ? highestLevel
      : null;
  });

  //Rough PDA estimate from the category subtotals already present on the voyage summary —
  //avoids an extra fetch per card; the authoritative total lives behind "Ver desglose" in Stop Overview.
  estimatedPdaCost = computed(() => {
    const pc = this.portCall();
    if (!pc) return null;
    const port = pc.portExpensesUSD ?? 0;
    const stevedoring = pc.stevedoringExpensesUSD ?? 0;
    const agency = pc.agencyExpensesUSD ?? 0;
    const total = port + stevedoring + agency;
    return total > 0 ? total : null;
  });

  overtimeTooltip = computed(() => {
    const snapshot = this.portCall()?.overtimeSnapshot;
    const level = this.overtimeLevel();
    if (!snapshot || !level) {
      return '';
    }

    const levelLabel = this.translateService.instant(`voyage.overtimeLevel.${level}`);
    return this.translateService.instant('voyage.overtimeTooltip', {
      level: levelLabel,
      regular: snapshot.regularHours.toFixed(1),
      ot1: snapshot.ot1Hours.toFixed(1),
      ot2: snapshot.ot2Hours.toFixed(1),
    });
  });

  //Events
  onChangeAsActive(): void {
    this.changeAsActive.emit();
  }

  onMoreOptions(rowId: number, event: MouseEvent): void {
    //Skip propagation to parent element
    event.preventDefault();
    event.stopPropagation();

    //Open dropdown
    this.dropdownService.openDropdown(rowId);
  }

  @HostListener('document:click')
  closeSubmenu(): void {
    this.dropdownService.closeDropdown();
  }

  triggerAction(action: any, portCall: PortCallModel): void {
    // Handle custom actions (objects with value property)
    if (action && typeof action === 'object' && 'value' in action) {
      const actionValue = action.value;

      if (actionValue === VoyagePortCallStates.PENDING || actionValue === VoyagePortCallStates.TO_BE_CONFIRMED) {
        // Show confirmation dialog
        const newStatusLabel = this.translateService.instant(`portCallStatus.${actionValue}`);
        const currentStatusLabel = this.translateService.instant(`portCallStatus.${portCall.status}`);

        this.confirmService.openConfirm({
          title: this.translateService.instant('voyage.confirmStatusChangeTitle'),
          message: this.translateService.instant('voyage.confirmStatusChangeMessage', {
            currentStatus: currentStatusLabel,
            newStatus: newStatusLabel
          }),
        }).subscribe((confirmed) => {
          if (confirmed) {
            const updatedPortCall = new PortCallModel().fromJSON({
              ...portCall,
              status: actionValue as VoyagePortCallStates,
            });
            this.changeStatus.emit(updatedPortCall);
          }
        });
        return;
      }

      // Handle add expenses
      if (actionValue === 'addExpenses') {
        this.onAddExpenses(portCall);
        return;
      }

      if (actionValue === 'addSpeedSelection') {
        this.onAddSpeedSelection(portCall);
        return;
      }

      if (actionValue === 'editNauticalMileDistance') {
        this.onEditNauticalMileDistance(portCall);
        return;
      }
    }

    if (action === 'addExpenses') {
      this.onAddExpenses(portCall);
      return;
    }

    // Handle add speed selection (fallback for direct string calls if any)
    if (action === 'addSpeedSelection') {
      this.onAddSpeedSelection(portCall);
      return;
    }

    // Handle standard table actions
    switch (action) {
      case TableAction.EDIT:
        this.editPortCallAction.emit();
        break;
      case TableAction.DELETE:
        this.deletePortCallAction.emit();
        break;
    }
  }

  extraDefaultActions = computed(() => {
    return [
      {
        action: TableAction.EDIT,
        label: 'table.edit',
        icon: 'icon-edit',
        class: 'c-btn--link',
      },
      {
        action: TableAction.DELETE,
        label: 'table.delete',
        icon: 'icon-delete',
        class: 'c-btn--link context:error',
      },
    ];
  });

  extraCustomActions = computed(() => {
    const customActions: any[] = [];

    //Add Expenses action
    customActions.push({
      value: 'addExpenses',
      label: 'voyage.managePda',
      icon: 'fa-solid fa-dollar-sign',
      class: 'c-link',
    });

    customActions.push({
      value: 'addSpeedSelection',
      label: 'voyage.addSpeedSelection',
      icon: 'fa-solid fa-gauge-high',
      class: 'c-link',
    });

    customActions.push({
      value: 'editNauticalMileDistance',
      label: 'voyage.editNauticalMiles',
      icon: 'fa-solid fa-pen-to-square',
      class: 'c-link',
    });

    // Add status toggle only if no actual arrival or departure dates are set
    const currentPortCall = this.portCall();
    if (currentPortCall && !currentPortCall.actualArrival && !currentPortCall.actualDeparture) {
      const currentStatus = currentPortCall.status;
      const isPending = currentStatus === VoyagePortCallStates.PENDING;
      const isToBeConfirmed = currentStatus === VoyagePortCallStates.TO_BE_CONFIRMED;

      // Only show toggle if current status is either PENDING or TO_BE_CONFIRMED
      if (isPending || isToBeConfirmed) {
        customActions.push({
          value: isPending ? VoyagePortCallStates.TO_BE_CONFIRMED : VoyagePortCallStates.PENDING,
          label: isPending ? 'portCallStatus.to_be_confirmed' : 'portCallStatus.pending',
          icon: 'fa-classic fa-solid fa-arrows-rotate',
          class: 'c-link',
        });
      }
    }

    return customActions;
  });

  onAddExpenses(portCall: PortCallModel): void {
    this.addExpensesAction.emit(portCall);
  }

  onAddSpeedSelection(portCall: PortCallModel): void {
    this.addSpeedSelectionAction.emit(portCall);
  }

  onEditNauticalMileDistance(portCall: PortCallModel): void {
    this.editNauticalMileDistanceAction.emit(portCall);
  }
}
